import palette from "assets/theme/base/palette";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";
import FaqData from "pages/faq/faqData";

const HELP_TOPICS = [
  {
    icon: "person_add",
    color: "#2196f3",
    bgcolor: "#e3f2fd",
    titleKey: "support.helpTopics.gettingStarted.title",
    descKey: "support.helpTopics.gettingStarted.desc",
  },
  {
    icon: "verified_user",
    color: "#4caf50",
    bgcolor: "#e8f5e9",
    titleKey: "support.helpTopics.accountLogin.title",
    descKey: "support.helpTopics.accountLogin.desc",
  },
  {
    icon: "receipt_long",
    color: "#9c27b0",
    bgcolor: "#f3e5f5",
    titleKey: "support.helpTopics.paymentsBilling.title",
    descKey: "support.helpTopics.paymentsBilling.desc",
  },
  {
    icon: "emoji_events",
    color: "#ff9800",
    bgcolor: "#fff3e0",
    titleKey: "support.helpTopics.featuresUsage.title",
    descKey: "support.helpTopics.featuresUsage.desc",
  },
];

const STEPS = [
  {
    num: 1,
    icon: "person_add",
    color: "#2196f3",
    bgcolor: "#e3f2fd",
    titleKey: "support.steps.addChild.title",
    descKey: "support.steps.addChild.desc",
  },
  {
    num: 2,
    icon: "credit_card",
    color: "#4caf50",
    bgcolor: "#e8f5e9",
    titleKey: "support.steps.completePayment.title",
    descKey: "support.steps.completePayment.desc",
  },
  {
    num: 3,
    icon: "mark_email_read",
    color: "#9c27b0",
    bgcolor: "#f3e5f5",
    titleKey: "support.steps.checkEmail.title",
    descKey: "support.steps.checkEmail.desc",
  },
  {
    num: 4,
    icon: "lock",
    color: "#ff9800",
    bgcolor: "#fff3e0",
    titleKey: "support.steps.loginAndSetPassword.title",
    descKey: "support.steps.loginAndSetPassword.desc",
  },
];

const Support = () => {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const email = profile?.email || "";
  const [expanded, setExpanded] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const supportEmail = "support@maxu.co";

  const handleChange = (panel) => (_, isExpanded) => setExpanded(isExpanded ? panel : "");

  const allFaqs = FaqData();
  const preview = allFaqs.slice(0, 8);
  const LEFT_FAQS = preview
    .slice(0, 4)
    .map((f, i) => ({ id: `faq-l${i}`, question: f.question, answer: f.answer }));
  const RIGHT_FAQS = preview
    .slice(4, 8)
    .map((f, i) => ({ id: `faq-r${i}`, question: f.question, answer: f.answer }));

  const mailtoHref = `mailto:${supportEmail}?subject=${encodeURIComponent(
    t("support.subject", { email })
  )}&body=${encodeURIComponent(t("support.body"))}`;

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.support")} />

      <MDBox pt={2} pb={4}>
        {/* ── Need Help Hero Card ── */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            background: "#fff",
          }}
        >
          <Grid container>
            <Grid item xs={12} md={7}>
              <MDBox p={4}>
                <MDTypography variant="h4" fontWeight="bold" mb={1}>
                  {t("support.heroTitle")}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={0.5}>
                  {t("support.assistantText")}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={0.5}>
                  {t("support.emailText")}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={0.5}>
                  {t("support.instructions")}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={3}>
                  {t("support.thanks")}
                </MDTypography>

                <MDBox display="flex" gap={1.5} flexWrap="wrap">
                  <MDButton
                    component="a"
                    href={mailtoHref}
                    variant="contained"
                    color="primary"
                    startIcon={<Icon>email</Icon>}
                    sx={{ borderRadius: "10px", px: 2.5, textTransform: "none", fontWeight: 600 }}
                  >
                    {t("support.emailSupport")}
                  </MDButton>
                  <MDButton
                    component="a"
                    href={`mailto:${supportEmail}`}
                    variant="outlined"
                    startIcon={<Icon>alternate_email</Icon>}
                    sx={{
                      borderRadius: "10px",
                      px: 2.5,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: palette.neutral[300],
                      color: "text.primary",
                      "&:hover": { borderColor: palette.brand[400], bgcolor: palette.brand[50] },
                    }}
                  >
                    {supportEmail}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Grid>

            {/* Decorative right panel */}
            <Grid item xs={12} md={5}>
              <MDBox
                sx={{
                  height: "100%",
                  minHeight: 160,
                  bgcolor: palette.brand[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Background circles */}
                <MDBox
                  sx={{
                    position: "absolute",
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    bgcolor: `${palette.brand[100]}80`,
                    top: -40,
                    right: -40,
                  }}
                />
                <MDBox
                  sx={{
                    position: "absolute",
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    bgcolor: `${palette.brand[200]}50`,
                    bottom: -30,
                    left: 20,
                  }}
                />

                {/* Support illustration using icons */}
                <MDBox sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                  <MDBox
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      bgcolor: palette.brand[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 1,
                      boxShadow: `0 8px 24px ${palette.brand[200]}`,
                    }}
                  >
                    <Icon sx={{ fontSize: "44px !important", color: "#fff" }}>support_agent</Icon>
                  </MDBox>
                  <MDBox display="flex" justifyContent="center" gap={2}>
                    <MDBox
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                      }}
                    >
                      <Icon sx={{ fontSize: "20px !important", color: palette.brand[500] }}>
                        chat_bubble
                      </Icon>
                    </MDBox>
                    <MDBox
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                      }}
                    >
                      <Icon sx={{ fontSize: "20px !important", color: "#ff9800" }}>
                        help_outline
                      </Icon>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </Card>

        {/* ── Search Bar ── */}
        {/* <MDBox mb={3}>
          <MDInput
            fullWidth
            placeholder="Search help articles, topics or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "text.secondary", fontSize: "20px !important" }}>search</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              },
            }}
          />
        </MDBox> */}

        {/* ── Popular Help Topics ── */}
        {/* <MDBox mb={3}>
          <MDTypography variant="h6" fontWeight="bold" mb={2} sx={{ color: palette.brand[700] }}>
            Popular Help Topics
          </MDTypography>
          <Grid container spacing={2}>
            {HELP_TOPICS.map((topic) => (
              <Grid item xs={12} sm={6} md={3} key={topic.titleKey}>
                <Card
                  sx={{
                    p: 2.5, borderRadius: 3, height: "100%", cursor: "pointer",
                    border: `1px solid ${palette.neutral[200]}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    transition: "all 200ms ease",
                    "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.12)", transform: "translateY(-2px)", borderColor: topic.color + "40" },
                  }}
                >
                  <MDBox sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: topic.bgcolor, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
                    <Icon sx={{ color: topic.color, fontSize: "26px !important" }}>{topic.icon}</Icon>
                  </MDBox>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5}>{t(topic.titleKey)}</MDTypography>
                  <MDTypography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>{t(topic.descKey)}</MDTypography>
                  <MDBox display="flex" justifyContent="flex-end" mt={1.5}>
                    <Icon sx={{ color: palette.neutral[400], fontSize: "18px !important" }}>chevron_right</Icon>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox> */}

        {/* ── Frequently Asked Questions ── */}
        <MDBox mb={3}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: palette.brand[700] }}>
              {t("support.faqTitle")}
            </MDTypography>
            <MDBox
              component={Link}
              to="/faq"
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ textDecoration: "none", cursor: "pointer" }}
            >
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: palette.brand[500] }}>
                {t("support.viewAllFaqs")}
              </MDTypography>
              <Icon sx={{ fontSize: "16px !important", color: palette.brand[500] }}>
                arrow_forward
              </Icon>
            </MDBox>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {LEFT_FAQS.map((faq) => (
                <Accordion
                  key={faq.id}
                  expanded={expanded === faq.id}
                  onChange={handleChange(faq.id)}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: `1px solid ${palette.neutral[200]}`,
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Icon sx={{ fontSize: "20px !important", color: "text.secondary" }}>
                        keyboard_arrow_down
                      </Icon>
                    }
                    sx={{
                      px: 2,
                      py: 0.5,
                      bgcolor: expanded === faq.id ? palette.neutral[50] : "#fff",
                    }}
                  >
                    <MDTypography variant="body2" fontWeight="medium">
                      {faq.question}
                    </MDTypography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 2, bgcolor: "#fff" }}>
                    <Divider sx={{ mb: 1.5 }} />
                    {Array.isArray(faq.answer) ? (
                      <MDBox component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {faq.answer.map((line, i) => (
                          <MDTypography
                            key={i}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.7, mb: 0.5 }}
                          >
                            {line}
                          </MDTypography>
                        ))}
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </MDTypography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              {RIGHT_FAQS.map((faq) => (
                <Accordion
                  key={faq.id}
                  expanded={expanded === faq.id}
                  onChange={handleChange(faq.id)}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: `1px solid ${palette.neutral[200]}`,
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Icon sx={{ fontSize: "20px !important", color: "text.secondary" }}>
                        keyboard_arrow_down
                      </Icon>
                    }
                    sx={{
                      px: 2,
                      py: 0.5,
                      bgcolor: expanded === faq.id ? palette.neutral[50] : "#fff",
                    }}
                  >
                    <MDTypography variant="body2" fontWeight="medium">
                      {faq.question}
                    </MDTypography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 2, bgcolor: "#fff" }}>
                    <Divider sx={{ mb: 1.5 }} />
                    {Array.isArray(faq.answer) ? (
                      <MDBox component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {faq.answer.map((line, i) => (
                          <MDTypography
                            key={i}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.7, mb: 0.5 }}
                          >
                            {line}
                          </MDTypography>
                        ))}
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </MDTypography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </MDBox>

        {/* ── Step-by-Step Guide ── */}
        <MDBox mb={3}>
          <MDTypography variant="h6" fontWeight="bold" mb={0.5} sx={{ color: palette.brand[700] }}>
            {t("support.stepByStepGuide")}
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            {t("support.stepByStepDescription")}
          </MDTypography>

          <Grid container spacing={2} alignItems="stretch">
            {STEPS.map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={step.num} sx={{ display: "flex" }}>
                <MDBox sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
                  <Card
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      flex: 1,
                      border: `1px solid ${palette.neutral[200]}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      position: "relative",
                    }}
                  >
                    {/* Step number badge */}
                    <MDBox
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: step.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1.5,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#fff", fontSize: "0.75rem" }}
                      >
                        {step.num}
                      </MDTypography>
                    </MDBox>

                    {/* Icon */}
                    <MDBox
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: step.bgcolor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1.5,
                      }}
                    >
                      <Icon sx={{ color: step.color, fontSize: "28px !important" }}>
                        {step.icon}
                      </Icon>
                    </MDBox>

                    <MDTypography variant="subtitle2" fontWeight="bold" mb={0.75}>
                      {t(step.titleKey)}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {t(step.descKey)}
                    </MDTypography>
                  </Card>

                  {/* Connector dashes between steps */}
                  {i < STEPS.length - 1 && (
                    <MDBox
                      sx={{
                        display: { xs: "none", md: "flex" },
                        alignItems: "center",
                        px: 0.5,
                        flexShrink: 0,
                        position: "absolute",
                        right: -12,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    ></MDBox>
                  )}
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </MDBox>

        {/* ── Still Need Help Banner ── */}
        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${palette.neutral[200]}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: palette.brand[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: "28px !important", color: palette.brand[500] }}>
                  headset_mic
                </Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold">
                  {t("support.stillNeedHelp")}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  {t("support.supportTeamText")}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  {t("support.emailUsAt")}{" "}
                  <MDBox
                    component="a"
                    href={`mailto:${supportEmail}`}
                    sx={{ color: palette.brand[500], fontWeight: 600, textDecoration: "none" }}
                  >
                    {supportEmail}
                  </MDBox>{" "}
                  {t("support.andWellGetBack")}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDButton
              component="a"
              href={mailtoHref}
              variant="contained"
              color="primary"
              startIcon={<Icon>email</Icon>}
              sx={{ borderRadius: "12px", px: 3, py: 1.25, fontWeight: 700, textTransform: "none" }}
            >
              {t("support.contactSupport")}
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default Support;
